// 1. React
import React, { useState } from 'react';
// 2. MUI components
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Box, Typography, Button, IconButton, TextField, CircularProgress,
} from '@mui/material';
// 3. MUI icons
import { Close, LocalShipping } from '@mui/icons-material';
// 5. RTK Query hooks
import { useUpdateOrderMutation } from '../../services/orderApi';
// 7. Project utilities
import { useToast } from '../common/ToastProvider';

const fmt = (v) => `₹${parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Blocking gate in front of checkout for a GSTIN sale above the e-way bill
// threshold. NOTE: saving here calls PATCH /orders/:id with transporter_name/
// vehicle_number/lr_number/distance_km — the backend (Pos::OrdersController
// #order_params) does not yet permit these fields, so this call will 422/
// silently drop them until that's added. See the wireframe's Open Questions.
export default function TransportDetailsDialog({ open, onClose, orderId, grandTotal, onSaved }) {
  const showToast = useToast();
  const [updateOrder, { isLoading }] = useUpdateOrderMutation();

  const [transporterName, setTransporterName] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [lrNumber, setLrNumber] = useState('');
  const [distanceKm, setDistanceKm] = useState('');
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const validate = () => {
    const e = {};
    if (!transporterName.trim()) e.transporterName = 'Transporter name is required';
    if (!vehicleNumber.trim()) e.vehicleNumber = 'Vehicle number is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await updateOrder({
        id: orderId,
        transporter_name: transporterName.trim(),
        vehicle_number: vehicleNumber.trim(),
        ...(lrNumber.trim() ? { lr_number: lrNumber.trim() } : {}),
        ...(distanceKm !== '' ? { distance_km: Number(distanceKm) } : {}),
      }).unwrap();
      showToast('Transport details saved', 'success');
      setTransporterName(''); setVehicleNumber(''); setLrNumber(''); setDistanceKm(''); setErrors({});
      onSaved();
    } catch (err) {
      showToast(err?.data?.error || 'Failed to save transport details. Please try again.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '12px', m: { xs: 2, sm: 3 } } }}>
      <DialogTitle sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShipping sx={{ fontSize: 20, color: '#4F46E5' }} />
          <Typography fontWeight={800} fontSize="0.95rem" color="#1E293B">Transport Details Required</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={isLoading} sx={{ color: '#94A3B8', mr: -0.5 }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 2.5, py: 2 }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mb: 2, lineHeight: 1.6 }}>
          This sale is {fmt(grandTotal)} to a GSTIN customer — an e-way bill is required before checkout.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth size="small" label="Transporter Name" required
            value={transporterName} onChange={(e) => setTransporterName(e.target.value)}
            error={!!errors.transporterName} helperText={errors.transporterName}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth size="small" label="Vehicle Number" required
            value={vehicleNumber} onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
            error={!!errors.vehicleNumber} helperText={errors.vehicleNumber}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth size="small" label="LR Number (optional)"
            value={lrNumber} onChange={(e) => setLrNumber(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth size="small" type="number" label="Distance (km) (optional)"
            value={distanceKm} onChange={(e) => setDistanceKm(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button onClick={handleClose} disabled={isLoading}
          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', color: '#64748B' }}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={handleSubmit} disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 800, fontSize: '0.82rem', bgcolor: '#4F46E5', borderRadius: '8px', '&:hover': { bgcolor: '#4338CA' } }}>
          {isLoading ? 'Saving…' : 'Save & Checkout'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
